import type * as Sentry from '@sentry/browser';
import type { PathOptions } from 'leaflet';
import z from 'zod';
import type { Messages } from '@/translations/messagesInterface.js';

export const LatLonSchema = z.object({
  lat: z.number(),
  lon: z.number(),
});

// Wire format: ISO 8601 datetime string like "2026-05-17T12:00:48.000Z".
// z.output → Date. Also accepts a `Date` so values that were parsed once and
// then round-tripped through structured clone (e.g. read back from IndexedDB)
// re-validate instead of failing on the already-transformed representation.
export const IsoDateSchema = z
  .union([z.iso.datetime(), z.date()])
  .transform((v) => new Date(v));

export type LatLon = z.infer<typeof LatLonSchema>;

declare global {
  interface Window {
    fbAsyncInit?: () => void;
    preventMapClick?: boolean;
    fmEmbedded: boolean;
    isRobot: boolean;
    fmHeadless?: {
      searchResultStyle?: PathOptions;
    };
    Sentry?: Pick<typeof Sentry, 'captureException' | 'captureMessage'>;
    fmStarted?: boolean;
    applyTheme: (theme?: 'dark' | 'light' | 'auto') => void;
    localStorageFallback?: StorageFallback;
  }

  interface ServiceWorkerGlobalScope {
    __WB_MANIFEST: { revision: string; url: string }[];
  }

  interface Window {
    _paq: {
      push: (
        args:
          | [
              'trackEvent',
              category: string,
              action: string,
              name?: string | number,
              value?: string | number,
            ]
          | ['setCookieConsentGiven']
          | ['setUserId', userId: string]
          | ['resetUserId']
          | ['trackPageView']
          | ['appendToTrackingUrl', appendToUrl: string],
      ) => void;
    };
  }
}

type IsTuple<T extends unknown[]> = number extends T['length'] ? false : true;

export type StringDates<T> = T extends Date
  ? string
  : T extends (infer U)[]
    ? IsTuple<T> extends true
      ? { [K in keyof T]: StringDates<T[K]> } // tuple: preserve shape
      : StringDates<U>[] //  array
    : T extends (infer U)[]
      ? StringDates<U>[] // mutable array
      : T extends object
        ? { [K in keyof T]: StringDates<T[K]> } // plain object
        : T;

// see https://stackoverflow.com/questions/58434389/typescript-deep-keyof-of-a-nested-object/58436959#58436959

type Prev = [
  never,
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  ...0[],
];

type Join<K, P> = K extends string | number
  ? P extends string | number
    ? `${K}${'' extends P ? '' : '.'}${P}`
    : never
  : never;

export type Leaves<T, D extends number = 10> = [D] extends [never]
  ? never
  : T extends Record<string, unknown>
    ? { [K in keyof T]-?: Join<K, Leaves<T[K], Prev[D]>> }[keyof T]
    : '';

export type MessagePaths = Leaves<Messages>;

/** Like `Leaves`, but only paths ending at a string. */
export type StringLeaves<T, D extends number = 10> = [D] extends [never]
  ? never
  : T extends string
    ? ''
    : T extends Record<string, unknown>
      ? { [K in keyof T]-?: Join<K, StringLeaves<T[K], Prev[D]>> }[keyof T]
      : never;

export interface OffscreenCanvas extends EventTarget {
  getContext: (contextId: '2d') => CanvasRenderingContext2D;
}

export const ShortcutSchema = z.object({
  code: z.string(), // event.code of main key
  ctrl: z.boolean().optional(),
  shift: z.boolean().optional(),
  alt: z.boolean().optional(),
  meta: z.boolean().optional(),
});

export type Shortcut = z.infer<typeof ShortcutSchema>;

export type DistributiveOmit<T, K extends keyof T> = T extends unknown
  ? Omit<T, K>
  : never;

export type DistributivePick<T, K extends keyof T> = T extends unknown
  ? Pick<T, K>
  : never;
